import { By } from 'selenium-webdriver';
import { ToolbarWebElementBase } from './ToolbarWebElementBase';
import {
  ButtonWebElement,
  SplitMenuButtonWebElement,
  MenuButtonWebElement,
  ProgressButtonWebElement,
  ButtonsetSingleWebElement,
  ButtonsetMultipleWebElement,
  ToggleButtonWebElement
} from '../../../webdriver';

/**
 * The component WebElement for [oj-c-toolbar](../../jsdocs/oj-c.Toolbar.html).
 * Do not instantiate this class directly, instead, use
 * [findToolbar](../functions/findToolbar.html).
 */
export class ToolbarWebElement extends ToolbarWebElementBase {
  // Put overrides here
  /**
   * Gets the value of <code>items</code> property.
   * Specifies the content to be placed into the toolbar.
   * @return The value of <code>items</code> property.
   *
   */
  public getItems(): Promise<Array<object>> {
    return this.getProperty<Array<object>>('items');
  }

  /**
   * Performs action of toolbar item specified by key.
   * @param itemLocation object containing key of the item to be activated.
   * Used for triggering the action on an item of type 'button' or 'progress-button', triggering the action on the button portion of an item of type 'oj-c-split-menu-button', or triggering the action on a menu item inside of an item of type 'menu-button' or 'split-menu-button'.
   * For changing the selection state of an item of type buttonset-single or buttonset-multiple, changing the selection state of a menu selection group inside of an item of type oj-c-menu-button or oj-c-split-menu-button, or changing the value of an item of type toggle-button, use the changeToolbarSelection method.
   * @override
   * @typeparam string
   * @param path optional object of label or labels, only needed for menu items
   */
  async doToolbarAction(
    itemLocator: { key: string },
    path?: { label: string | string[]; key?: never }
  ): Promise<void> {
    const toolbarItem = await this.findElement(
      By.css(`[data-oj-private-key="${itemLocator.key}"]`)
    );
    const elementType = toolbarItem.constructor;

    switch (elementType) {
      case ButtonWebElement:
      case ProgressButtonWebElement:
        if (path) {
          throw Error(
            `A path was supplied, but item types of 'button' or 'progress-button' do not support menus`
          );
        } else {
          await (toolbarItem as ButtonWebElement | ProgressButtonWebElement).doAction();
        }
        break;
      case MenuButtonWebElement:
        if (path) {
          await (toolbarItem as MenuButtonWebElement).doMenuAction(path.label);
        }
        break;
      case SplitMenuButtonWebElement:
        if (path) {
          await (toolbarItem as SplitMenuButtonWebElement).doMenuAction(path.label);
        } else {
          await (toolbarItem as SplitMenuButtonWebElement).doAction();
        }
        break;
      case ButtonsetSingleWebElement:
      case ButtonsetMultipleWebElement:
      case ToggleButtonWebElement:
        throw Error(
          `The toolbar item found for the itemLocator ${JSON.stringify(
            itemLocator,
            null,
            2
          )} is of item type 'buttonset-single', 'buttonset-multiple', or 'toggle-button', which do not support actions. Try using the changeToolbarSelection method instead.`
        );
    }
  }
}
